import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useUpdateTeamMutation } from "~/shared/api/team/team.grpc";
import { useRoleTeams } from "~/shared/lib/driver-teams";
import { teamMembersWith } from "../model/use-user-form";
import { ChipsPickerField } from "./chips-picker-field";

type Team = ConnectProto.Team;

const toItem = (team: Team) => ({ id: String(team.id), name: team.name });

function TeamsPicker(props: {
  teams: Team[];
  isMember: (team: Team) => boolean;
  onChange: (team: Team, member: boolean) => void;
  busy?: boolean;
}) {
  const t = useT();
  const { teams, isMember, onChange } = props;
  const set = (member: boolean) => (id: string) => {
    const team = teams.find((x) => String(x.id) === id);
    if (team) onChange(team, member);
  };

  return (
    <ChipsPickerField
      chips={teams.filter(isMember).map(toItem)}
      options={teams.filter((team) => !isMember(team)).map(toItem)}
      onAdd={set(true)}
      onRemove={set(false)}
      busy={props.busy}
      inputId="user-team-search"
      texts={{
        add: t(Namespace.Employees, "create.teams.add"),
        search: t(Namespace.Employees, "create.teams.search"),
        empty: t(Namespace.Employees, "create.teams.empty"),
        noResults: t(Namespace.Employees, "create.teams.noResults"),
        remove: (name) => t(Namespace.Employees, "create.teams.remove", { name }),
      }}
    />
  );
}

/** Edit: team membership applies immediately, independent of the form's Save. */
export function TeamsField({ user }: { user: ConnectProto.User }) {
  const teams = useRoleTeams(user.ownerOrganizationId, user.role);
  const [updateTeam, { isLoading }] = useUpdateTeamMutation();

  return (
    <TeamsPicker
      teams={teams}
      isMember={(team) => team.users.some((u) => u.id === user.id)}
      // ponytail: user_ids replaces the whole membership, so two dispatchers editing the
      // same team at once can lose one change; an add/remove-member RPC would fix it.
      onChange={(team, member) =>
        updateTeam({ id: team.id, userIds: teamMembersWith(team, user.id, member) })
      }
      busy={isLoading}
    />
  );
}

/** Create: the picked team ids are kept by the form and joined once the user exists. */
export function TeamsDraftField(props: {
  ownerOrganizationId: bigint;
  role: ConnectProto.UserRole;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { value, onChange } = props;
  const teams = useRoleTeams(props.ownerOrganizationId, props.role);

  return (
    <TeamsPicker
      teams={teams}
      isMember={(team) => value.includes(String(team.id))}
      onChange={(team, member) =>
        onChange(
          member ? [...value, String(team.id)] : value.filter((id) => id !== String(team.id)),
        )
      }
    />
  );
}
